import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { RouterLink } from "@angular/router";
import { MatIcon } from "@angular/material/icon";

import { SemaforoBsc } from "src/app/shared/model/bsc/bsc.model";
import { PistaBarra, enPista } from "../bsc-calculos";

/**
 * Un tile de .tickets-kpis (calco de DashboardKpi de dashboard-graficas.ts
 * con nota, barra y enlace). Lo arma la pantalla UNA vez por respuesta.
 */
export interface TileBsc {
  etiqueta: string;
  valor: string;
  meta: string;
  icono: string;
  /** ticket-kpi--done | --active | --time | --total | --muted */
  clase: string;
  /** Segundo renglón; `notaClase` lo pinta en el tono del semáforo. */
  nota?: string;
  notaClase?: "is-exito" | "is-aviso" | "is-peligro";
  /** La barrita, real contra programado (avance): pasado lo programado, llena la pista y marca la meta. */
  barra?: PistaBarra;
  barraClase?: string;
  /** El valor es una palabra ("Vencida"), no un número: se pinta más chico. */
  compacto?: boolean;
  /** Un enlace dentro del módulo; `queryParams` para conservar año y corte. */
  enlace?: { texto: string; ruta: string; queryParams?: Record<string, string | number | null> };
}

/**
 * BSC · la fila de tiles de A1 y A2 (F4.2, separada en F4.3 para que las dos
 * pantallas no repitan el marcado ni el CSS). Marcado .tickets-kpis /
 * .ticket-kpi de dashboard/shared/dashboard-tarjetas.css, que no es global:
 * va en styleUrls, como en dashini. La pantalla pone el componente DENTRO de
 * un .tickets-dashboard, porque de esa clase cuelgan en
 * src/temas/tema-contenido.css los colores de oscuro y cristal.
 */
@Component({
  selector: "app-tiles-bsc",
  standalone: true,
  imports: [CommonModule, RouterLink, MatIcon],
  template: `
    <section class="tickets-kpis" [attr.aria-label]="etiqueta">
      <article class="ticket-kpi" *ngFor="let t of tiles; trackBy: trackTile" [ngClass]="t.clase">
        <div class="ticket-kpi__icon">
          <mat-icon aria-hidden="true">{{ t.icono }}</mat-icon>
        </div>
        <div class="bsc-tile__cuerpo">
          <span>{{ t.etiqueta }}</span>
          <strong [class.bsc-tile__valor-texto]="t.compacto">{{ t.valor }}</strong>
          <small>{{ t.meta }}</small>
          <div class="bsc-tile__barra" *ngIf="t.barra != null" aria-hidden="true">
            <i [ngClass]="t.barraClase" [style.width.%]="t.barra.ancho"></i>
            <b class="bsc-raya" *ngIf="t.barra.raya != null" [style.left.%]="t.barra.raya"></b>
          </div>
          <p class="bsc-tile__nota" *ngIf="t.nota" [ngClass]="t.notaClase">{{ t.nota }}</p>
          <a class="bsc-tile__enlace" *ngIf="t.enlace" [routerLink]="t.enlace.ruta"
             [queryParams]="t.enlace.queryParams ?? null">{{ t.enlace.texto }}</a>
        </div>
      </article>
    </section>
  `,
  styleUrls: ["../../dashboard/shared/dashboard-tarjetas.css", "../bsc.shared.css", "./tiles-bsc.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TilesBscComponent {
  @Input() tiles: TileBsc[] = [];
  /** El nombre de la región para lectores de pantalla. */
  @Input() etiqueta = "Indicadores";

  trackTile(_: number, t: TileBsc): string {
    return t.etiqueta;
  }
}

/** El color del tile por semáforo (las variantes de dashboard-tarjetas.css). */
export function claseTile(semaforo: SemaforoBsc | null | undefined): string {
  switch (semaforo) {
    case "VERDE":
      return "ticket-kpi--done";
    case "AMARILLO":
      return "ticket-kpi--time";
    case "ROJO":
      return "ticket-kpi--total";
    default:
      return "ticket-kpi--active";
  }
}

/** El tono de la nota del tile por semáforo. */
export function claseNota(semaforo: SemaforoBsc | null | undefined): TileBsc["notaClase"] {
  switch (semaforo) {
    case "VERDE":
      return "is-exito";
    case "AMARILLO":
      return "is-aviso";
    case "ROJO":
      return "is-peligro";
    default:
      return undefined;
  }
}

/** El color de la barrita por semáforo; sin semáforo, el azul de "real". */
export function claseBarra(semaforo: SemaforoBsc | null | undefined): string {
  switch (semaforo) {
    case "VERDE":
      return "is-verde";
    case "AMARILLO":
      return "is-amarillo";
    case "ROJO":
      return "is-rojo";
    default:
      return "";
  }
}

/**
 * La barrita del tile: real contra programado; sin alguno de los dos, nada.
 * Hasta lo programado, el avance sin raya. Pasado (109 %), la barra llena la
 * pista y la raya marca dónde quedó la meta, para que se note el sobrecumplimiento.
 */
export function avance(real: number | null, programado: number | null): PistaBarra | undefined {
  if (real == null || !programado) {
    return undefined;
  }
  const razon = Math.max(0, real / programado);
  return razon > 1 ? enPista({ real: razon, programado: 1 }, razon) : { ancho: razon * 100, raya: null };
}
